import type { CopySourceCourse, ModuleItemType } from 'app/types/modules';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getCourseItemsService } from 'core/services/courseItems.service';
import { useEffect, useState } from 'react';

type UseCopySourceModulesParams = {
  course: CopySourceCourse | null;
};

type UseCopySourceModulesReturn = {
  hasError: boolean;
  isLoading: boolean;
  modules: ModuleItemType[];
};

/** Las dos ramas de `getCourseItemsService` devuelven shapes distintos. */
type CourseItemsResponse = { items?: ModuleItemType[] } | ModuleItemType[] | null;

/**
 * Módulos del curso elegido en el paso 2 del wizard "Copiar módulo" (TAN-10192).
 * Misma fuente de datos que el programa (overlays + personalización), para que
 * los nombres coincidan con lo que el profesorado ve en el curso de origen.
 */
function useCopySourceModules({ course }: UseCopySourceModulesParams): UseCopySourceModulesReturn {
  const { user } = useUser();
  const { enableOverlays } = useSystemSettings();

  const [modules, setModules] = useState<ModuleItemType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const courseGuid = course?.guid;
  // El servicio admite `undefined` por contrato de la API, no `null`.
  const schoolGuid = course?.school_guid ?? undefined;
  // `isDisregardedProgram` de `core/utils/user` es exactamente esto: los cursos
  // `template-1` no cuelgan de un grupo-clase y caen por la rama sin overlays.
  const isDisregardedProgram = !course?.school_group_guid;
  const userGuid = user?.guid;

  useEffect(() => {
    const requestedCourseGuid = courseGuid;
    if (!requestedCourseGuid) {
      setModules([]);
      setHasError(false);
      setIsLoading(false);
      return undefined;
    }

    // Cambiar de curso invalida la respuesta en vuelo del anterior.
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    // `enableOverlays` decide el endpoint: esperamos a que resuelvan los system
    // settings para no pedir la versión base del programa por error.
    if (enableOverlays === undefined) {
      return () => {
        cancelled = true;
      };
    }

    async function loadModules(): Promise<void> {
      try {
        // El wizard no dispone de `course.users[]` de un curso ajeno (el listado
        // de TAN-10195 no lo trae y no hay endpoint que lo reconstruya), así que
        // la personalización se pide para el profesor actual — acordado con
        // backend en TAN-10195 y cerrado en TAN-10192.
        const customizedBy = userGuid ? [userGuid] : [];
        const response = (await getCourseItemsService(requestedCourseGuid, schoolGuid, customizedBy, enableOverlays, isDisregardedProgram)) as CourseItemsResponse;
        if (cancelled) return;

        const items = Array.isArray(response) ? response : response?.items;
        if (!Array.isArray(items)) {
          // El interceptor global de axios resuelve los errores HTTP en vez de
          // rechazarlos: sin lista de módulos, la petición falló.
          setModules([]);
          setHasError(true);
          return;
        }

        setModules(items);
      } catch {
        if (cancelled) return;
        setModules([]);
        setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadModules();

    return () => {
      cancelled = true;
    };
  }, [courseGuid, enableOverlays, isDisregardedProgram, schoolGuid, userGuid]);

  return { hasError, isLoading, modules };
}

export default useCopySourceModules;
